'use client'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Dropdown from './Dropdown'
import CTASection from './CTASection'
import Icon from './Icon'
import { useLang } from '../context/LangContext'
import { t, cta as ctaT, productsDropdown } from '../lib/translations'
import lexoyaIcon from '../app/icon.svg'

const productKeys = ['vps', 'gpu', 'paas', 'database', 'storage', 'backup', 'security', 'devops', 'org', 'monitoring', 'consulting'] as const

const requestTypeKeys = ['consulting', 'price', 'organizational', 'other'] as const

export default function CTA() {
  const lang = useLang()
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({
    fullName: '',
    contactNumber: '',
    email: '',
    businessName: '',
    product: '',
    requestType: '',
    message: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const update = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!form.fullName.trim()) newErrors.fullName = 'Required'
    if (!form.contactNumber.trim()) newErrors.contactNumber = 'Required'
    if (!form.email.trim()) newErrors.email = 'Required'
    if (!form.businessName.trim()) newErrors.businessName = 'Required'
    if (!form.product) newErrors.product = 'Required'
    if (!form.requestType) newErrors.requestType = 'Required'
    if (!form.message.trim()) newErrors.message = 'Required'
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return
    
    setIsSubmitting(true)
    setSubmitStatus('idle')
    
    try {
      const res = await fetch('https://api.ir01.lexoya.com/v1/consulting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.fullName,
          phone: form.contactNumber,
          email: form.email,
          business_name: form.businessName,
          required_product: form.product,
          request_type: form.requestType,
          message: form.message
        })
      })
      
      if (!res.ok) throw new Error('API failed')
      
      setSubmitStatus('success')
      setTimeout(() => {
        setModalOpen(false)
        setForm({ fullName: '', contactNumber: '', email: '', businessName: '', product: '', requestType: '', message: '' })
        setSubmitStatus('idle')
      }, 2000)
    } catch (err) {
      console.error('Submission error:', err)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass = (field: string) =>
    `w-full bg-white/[0.04] border ${errors[field] ? 'border-red-500/50' : 'border-white/[0.08]'} rounded-lg px-4 py-2.5 text-sm text-white placeholder-[#475569] focus:outline-none focus:border-[rgb(var(--brand-rgb)/0.5)] transition-colors`

  const products = productKeys.map((k) => t(lang, productsDropdown[k].label))
  const requestTypes = requestTypeKeys.map((k) => t(lang, ctaT.requestTypes[k]))

  return (
    <>
      <CTASection id="cta" secondaryOnClick={() => setModalOpen(true)} />

      <AnimatePresence>
        {modalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setModalOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 10 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#0c0c1a]/95 backdrop-blur-xl border border-white/[0.08] p-8"
            >
              <button
                onClick={() => setModalOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-[#64748b] hover:text-white transition-colors cursor-pointer"
              >
                <Icon name="close" className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <img src={lexoyaIcon.src} alt="Lexoya" className="w-10 h-10 rounded-xl shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold">{t(lang, ctaT.formTitle)}</h3>
                  <p className="text-sm text-[#64748b]">{t(lang, ctaT.formSubtitle)}</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.fullName)}</label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={e => update('fullName', e.target.value)}
                    placeholder={t(lang, ctaT.placeholders.fullName)}
                    className={inputClass('fullName')}
                  />
                  {errors.fullName && <p className="text-xs text-red-400 mt-1">{errors.fullName}</p>}
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.contactNumber)}</label>
                  <input
                    type="tel"
                    value={form.contactNumber}
                    onChange={e => update('contactNumber', e.target.value)}
                    placeholder={t(lang, ctaT.placeholders.contactNumber)}
                    className={inputClass('contactNumber')}
                  />
                  {errors.contactNumber && <p className="text-xs text-red-400 mt-1">{errors.contactNumber}</p>}
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.email)}</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => update('email', e.target.value)}
                    placeholder={t(lang, ctaT.placeholders.email)}
                    className={inputClass('email')}
                  />
                  {errors.email && <p className="text-xs text-red-400 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.businessName)}</label>
                  <input
                    type="text"
                    value={form.businessName}
                    onChange={e => update('businessName', e.target.value)}
                    placeholder={t(lang, ctaT.placeholders.businessName)}
                    className={inputClass('businessName')}
                  />
                  {errors.businessName && <p className="text-xs text-red-400 mt-1">{errors.businessName}</p>}
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.product)}</label>
                  <Dropdown
                    value={form.product}
                    onChange={v => update('product', v)}
                    options={products}
                    placeholder={t(lang, ctaT.placeholders.product)}
                    error={errors.product}
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.requestType)}</label>
                  <Dropdown
                    value={form.requestType}
                    onChange={v => update('requestType', v)}
                    options={requestTypes}
                    placeholder={t(lang, ctaT.placeholders.requestType)}
                    error={errors.requestType}
                  />
                </div>

                <div>
                  <label className="block text-xs text-[#64748b] mb-1.5">{t(lang, ctaT.formLabels.message)}</label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={e => update('message', e.target.value)}
                    placeholder={t(lang, ctaT.placeholders.message)}
                    className={inputClass('message')}
                  />
                  {errors.message && <p className="text-xs text-red-400 mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || submitStatus === 'success'}
                  className={`w-full py-3 rounded-xl bg-gradient-to-r font-semibold text-sm transition-all duration-200 shadow-lg shadow-[rgb(var(--brand-rgb)/0.2)] ${
                    submitStatus === 'success'
                      ? 'from-emerald-500 to-emerald-600 cursor-default'
                      : submitStatus === 'error'
                      ? 'from-red-500 to-red-600 cursor-pointer'
                      : 'from-[var(--brand-600)] to-[var(--brand-500)] hover:from-[var(--brand-500)] hover:to-[var(--brand-300)] cursor-pointer btn-lightning'
                  } ${isSubmitting ? 'opacity-70 cursor-wait' : ''}`}
                >
                  {isSubmitting
                    ? t(lang, ctaT.submitBtn) + '...'
                    : submitStatus === 'success'
                    ? '✅'
                    : submitStatus === 'error'
                    ? '❌'
                    : t(lang, ctaT.submitBtn)}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
